import { Controller } from "@hotwired/stimulus"

// The clipping tool's two outside files: the drawing library (fabric.js 5,
// which this editor is written against; the story editor's bundled fabric is
// a later major version with a different API) and the Google Fonts sheet
// behind its text styles. They used to sit in the press clip page's <head>,
// where Turbo held the whole page until Google answered (2.8 s in a test run
// from a laptop, 2026-10-19) and a full page load waited on cdnjs as well.
// Nothing is drawn before Generate Clipping or Edit Clipping, so both now
// start downloading once the page has finished loading, and an editor opened
// before the library arrives waits for it (_waitForFabric).
const FABRIC_SRC = "https://cdnjs.cloudflare.com/ajax/libs/fabric.js/5.3.1/fabric.min.js"
const CLIP_FONTS_HREF = "https://fonts.googleapis.com/css2?family=Inter:wght@400;700&family=Playfair+Display:wght@400;700&family=Lora:wght@400;700&family=Montserrat:wght@400;700&family=Roboto+Slab:wght@400;700&family=Merriweather:wght@400;700&family=Oswald:wght@400;700&family=Bebas+Neue&family=Crimson+Text:wght@400;700&family=Source+Sans+3:wght@400;700&family=DM+Serif+Display&display=swap"
const CLIP_FONTS_ID = "clipping-editor-fonts"
const FABRIC_TIMEOUT_MS = 20000
// The upload box under Clipping Image needs neither file, so it is the way
// through when the editor cannot load at all (a blocked or broken network).
const FABRIC_FAILED_MESSAGE = "The clipping editor could not load. Close this and try again. If it happens twice, close this and upload a screenshot under Clipping Image instead."

let fabricLoading = null

// One download, shared by every editor that asks, until the next full page
// load. A failed one is forgotten, so the next editor that opens tries again.
// A stalled one (hotel wifi, a captive portal) counts as failed after 20 s
// instead of loading forever; a copy that arrives later still defines
// fabric, and the next try finds it.
function loadFabric() {
  if (window.fabric) return Promise.resolve()
  if (!fabricLoading) {
    const loading = new Promise((resolve, reject) => {
      const script = document.createElement("script")
      const fail = (message) => {
        clearTimeout(timer)
        if (window.fabric) return resolve()   // an earlier copy arrived late
        script.remove()
        reject(new Error(message))
      }
      const timer = setTimeout(() => fail("fabric.js did not arrive in 20 s"), FABRIC_TIMEOUT_MS)
      script.src = FABRIC_SRC
      script.onload = () => {
        clearTimeout(timer)
        if (window.fabric) resolve()
        else fail("fabric.js loaded but did not define fabric")
      }
      script.onerror = () => fail("fabric.js could not be downloaded")
      document.head.appendChild(script)
    })
    loading.catch(() => { if (fabricLoading === loading) fabricLoading = null })
    fabricLoading = loading
  }
  return fabricLoading
}

// The sheet stays in <head> across Turbo visits (Turbo keeps stylesheets), so
// it is added once. It only names the fonts; the two faces a new clip's text
// starts in are fetched as soon as it arrives, so they are usually ready
// before the title and description are first drawn.
function loadClipFonts() {
  if (document.getElementById(CLIP_FONTS_ID)) return
  const link = document.createElement("link")
  link.id = CLIP_FONTS_ID
  link.rel = "stylesheet"
  link.href = CLIP_FONTS_HREF
  link.onload = () => {
    document.fonts?.load('700 48px "Playfair Display"').catch(() => {})
    document.fonts?.load('400 24px Inter').catch(() => {})
  }
  document.head.appendChild(link)
}

// After the window's load event, so the downloads never hold it (the tab's
// spinner, and every test's visit, wait on it). A Turbo visit lands on a
// document that finished loading long ago, so they start at once there.
function afterPageLoad(callback) {
  if (document.readyState === "complete") callback()
  else window.addEventListener("load", callback, { once: true })
}

export default class extends Controller {
  static targets = [
    "canvas",
    "composeBar",
    "editBar",
    "advancedBar",
    "fontSelect",
    "fontSizeSelect",
    "colorPicker",
    "highlightPicker",
    "boldBtn",
    "italicBtn",
    "underlineBtn",
    "strikeBtn",
    "alignLeftBtn",
    "alignCenterBtn",
    "alignRightBtn",
    "outlineToggle",
    "outlineWidth",
    "outlineColor",
    "shadowToggle",
    "shadowBlur",
    "shadowOffsetX",
    "shadowOffsetY",
    "shadowColor",
    "opacitySlider",
    "opacityValue",
    "moreToggle",
    "status",
    "phaseLabel",
    "boxBorderToggle",
    "boxBorderWidth",
    "boxBorderColor",
    "boxBorderPadding",
    "imageMenuBtn",
    "imageInsertPanel",
    "insertImageUrl",
    "insertPhotoPicker",
    "insertPhotoGrid",
    "insertImageUpload",
    "overlaySelect",
    "clipBorderToggle",
    "clipBorderWidth",
    "clipBorderColor"
  ]

  static values = {
    photosUrl: String,
    proxyUrl: String
  }

  CANVAS_SIZE = 1080

  connect() {
    this.fabricCanvas = null
    this.phase = null
    this.bgImage = null
    this.currentTitle = ''
    this.currentDescription = ''
    this.currentLogoUrl = null
    this.history = []
    this.historyIndex = -1
    this.skipHistory = false
    // Paste-a-file rung of the fallback ladder (register 2026-08-04): an
    // outlet that bot-blocks the proxy still yields to copy-image + Cmd+V.
    // Document-level because the canvas never holds focus; guarded on an
    // open editor so it cannot fire on the page underneath.
    this._pasteHandler = (e) => this._handlePaste(e)
    document.addEventListener('paste', this._pasteHandler)

    afterPageLoad(() => {
      loadFabric().catch(() => {})   // an editor that opens reports a failure itself
      loadClipFonts()
    })
  }

  disconnect() {
    document.removeEventListener('paste', this._pasteHandler)
    this.close()
  }

  // The editor was closed (or left): any init still waiting, on the library
  // or on a background, is retired, so nothing is built behind a closed
  // editor and no failure is reported into one.
  close() {
    this._newInitToken()
    this.destroyCanvas()
  }

  // An editor is on screen. Save Clipping asks before it exports anything.
  get hasCanvas() {
    return !!this.fabricCanvas
  }

  // Save Clipping with no editor on screen. Said on the status line under
  // the canvas, beside the button, where the next build replaces it; the
  // banner keeps what it holds (after a failure, the way out).
  sayNotReady() {
    this._setStatus('The clipping editor has not loaded yet, so there is nothing to save.')
  }

  _handlePaste(event) {
    if (!this.fabricCanvas) return
    const items = [...(event.clipboardData?.items || [])]
    const image = items.find(i => i.kind === 'file' && i.type.startsWith('image/'))
    if (!image) return

    // A field takes text, never an image. After a refusal the cursor starts
    // in Title and the status line says "copy one from the article and
    // press Cmd+V", so a focus guard alone swallowed that exact paste
    // (reconvene 2026-09-22). A clipboard that ALSO carries plain text
    // (a Word selection, text copied with a picture in it) is still left
    // to the field she is typing in.
    const tag = document.activeElement?.tagName
    const inField = tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable
    if (inField && items.some(i => i.kind === 'string' && i.type === 'text/plain')) return

    const file = image.getAsFile()
    if (!file) return
    event.preventDefault()
    this._readFileAndInsert(file)
  }

  destroyCanvas() {
    if (this._keyHandler) {
      document.removeEventListener('keydown', this._keyHandler)
      this._keyHandler = null
    }
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler)
      this._resizeHandler = null
    }
    if (this.fabricCanvas) {
      this.fabricCanvas.dispose()
      this.fabricCanvas = null
    }
    this.phase = null
    this.bgImage = null
    this._textOnly = false
    this._overlayRect = null
    this._clipBorderEnabled = false
    this.history = []
    this.historyIndex = -1
  }

  // ════════════════════════════════════════════
  // CONTAINER MEASUREMENT
  // ════════════════════════════════════════════

  _getContainer() {
    let el = this.canvasTarget?.parentElement
    if (!el) return null
    if (el.classList && el.classList.contains('canvas-container')) {
      el = el.parentElement
    }
    return el
  }

  // ════════════════════════════════════════════
  // ZOOM / SIZING
  // ════════════════════════════════════════════

  _applyZoom() {
    if (!this.fabricCanvas) return
    const container = this._getContainer()
    if (!container) return
    const availableWidth = container.offsetWidth
    if (!availableWidth || availableWidth < 50) return
    const zoom = Math.min(availableWidth / this.CANVAS_SIZE, 1)
    this.fabricCanvas.setZoom(zoom)
    this.fabricCanvas.setDimensions({
      width: this.CANVAS_SIZE * zoom,
      height: this.CANVAS_SIZE * zoom
    })
    this.fabricCanvas.calcOffset()
  }

  recalcOffset() {
    if (this.fabricCanvas) {
      this._applyZoom()
    }
  }

  // ════════════════════════════════════════════
  // ENTRY POINT (called by parent controller)
  // ════════════════════════════════════════════

  // DEF-6 (register 2026-08-04): every init path takes a token; a slow
  // image load finishing AFTER a newer init started must discard itself
  // instead of rebuilding over (or drawing onto) the newer editor.
  _newInitToken() {
    this._initToken = (this._initToken || 0) + 1
    return this._initToken
  }

  _initStale(token) {
    return token !== this._initToken
  }

  // Called only while the drawing library has not arrived, so an editor
  // opened after it loaded (usually: the download starts when the page loads,
  // and Generate first waits on the article lookup) builds exactly as it did
  // when the library sat in the page's <head>, with no extra wait. False
  // means stop: the editor was closed or a newer init owns it, or the library
  // could not be downloaded and she has been told. The heading says so while it waits,
  // and the toolbars are hidden: the form's own "Step 1: Frame Your Image"
  // and its Lock button would offer a step that does nothing yet. Every path
  // that builds the editor sets its own heading and shows its own toolbar.
  async _waitForFabric(initToken) {
    loadClipFonts()   // when the page's load event has not come yet
    this._setPhaseLabel('Loading the clipping editor...')
    this._setStatus('')
    for (const bar of [this.composeBarTargets, this.editBarTargets, this.advancedBarTargets, this.imageInsertPanelTargets].flat()) {
      bar.classList.add('hidden')
    }
    try {
      await loadFabric()
    } catch (e) {
      console.error("Clipping editor library failed to load:", e)
      if (this._initStale(initToken)) return false
      this._fabricFailed = true
      this._setPhaseLabel('')
      this._reportImageFailure(FABRIC_FAILED_MESSAGE)
      return false
    }
    return !this._initStale(initToken)
  }

  // Every init is a retry of a failed library download, if there was one:
  // it takes down that failure, and only that message (the banner may show
  // another one by now). Called before the library check, because a copy
  // that arrived late lets the retry skip the wait.
  _clearFabricFailure() {
    if (!this._fabricFailed) return
    this._fabricFailed = false
    this.dispatch('imageLoaded', { detail: { clears: FABRIC_FAILED_MESSAGE }, bubbles: true })
  }

  async initEditor(imageUrl, title, description, logoUrl) {
    const initToken = this._newInitToken()
    this._clearFabricFailure()
    // Read BEFORE any wait: a logo uploaded, or words typed in Title or
    // Description, during the wait for the library or the background is
    // newer than what this call was handed (the logo, committee 2026-09-24;
    // the words, 2026-10-19: the cursor lands in Title as the wait starts).
    const logoAtStart = this.currentLogoUrl
    const titleAtStart = this.currentTitle
    const descriptionAtStart = this.currentDescription
    if (!window.fabric && !(await this._waitForFabric(initToken))) return false
    // DEF-4: LOAD BEFORE DESTROY. A failed background swap must never cost
    // the user the composition they already have. Previously the canvas was
    // torn down first, so a blocked image left them on a blank canvas with
    // no message (the "won't let me choose a different photo" report).
    let preloaded = null
    if (imageUrl) {
      try {
        preloaded = await this._loadImage(imageUrl)
      } catch (e) {
        console.error("Image load failed:", e)
        if (this._initStale(initToken)) return false
        this._reportImageFailure(
          "That image could not be loaded, so nothing was changed. Pick a DPK photo, or save the image and use Upload.",
          imageUrl
        )
        if (this.fabricCanvas) return false   // keep the existing composition
      }
    }
    if (this._initStale(initToken)) return false  // a newer init superseded this load

    // Words she typed straight onto the canvas survive a background swap:
    // picking a photo rebuilds the canvas, and rebuilding it from the fields
    // threw away an edit she made on the image. The canvas wins; the fields
    // fill in only what the canvas does not have. (The canvas is destroyed
    // when the editor closes, so a new link never inherits an old headline.)
    const typed = this._typedText()
    this.destroyCanvas()
    const newerTitle = this.currentTitle !== titleAtStart ? this.currentTitle : null
    const newerDescription = this.currentDescription !== descriptionAtStart ? this.currentDescription : null
    this.currentTitle = typed.title || newerTitle || title || 'Article Title'
    this.currentDescription = typed.description || newerDescription || description || 'Article description'
    // A logo chosen while the background loaded (Upload Logo, setLogo) is
    // newer than the one this call was handed (committee 2026-09-24).
    this.currentLogoUrl = this.currentLogoUrl !== logoAtStart ? this.currentLogoUrl : logoUrl

    const canvasEl = this.canvasTarget
    this.fabricCanvas = new fabric.Canvas(canvasEl, {
      width: this.CANVAS_SIZE,
      height: this.CANVAS_SIZE,
      backgroundColor: '#1a1a1a',
      preserveObjectStacking: true,
      selection: true
    })

    this.fabricCanvas.on('after:render', () => {
      this._drawClipBorder()
    })

    this._setupCenterGuides()

    // Extend Textbox to support box border rendering
    const originalRender = fabric.Textbox.prototype._render
    fabric.Textbox.prototype._render = function(ctx) {
      originalRender.call(this, ctx)
      if (this._boxBorderWidth && this._boxBorderWidth > 0 && this._boxBorderColor) {
        ctx.save()
        const pad = this._boxBorderPadding || 10
        const w = this.width + pad * 2
        const h = this.height + pad * 2
        ctx.strokeStyle = this._boxBorderColor
        ctx.lineWidth = this._boxBorderWidth
        ctx.strokeRect(-this.width / 2 - pad, -this.height / 2 - pad, w, h)
        ctx.restore()
      }
    }

    // Extend Image to support box border rendering
    const originalImageRender = fabric.Image.prototype._render
    fabric.Image.prototype._render = function(ctx) {
      originalImageRender.call(this, ctx)
      if (this._boxBorderWidth && this._boxBorderWidth > 0 && this._boxBorderColor) {
        ctx.save()
        const pad = this._boxBorderPadding || 10
        const w = this.width + pad * 2
        const h = this.height + pad * 2
        ctx.strokeStyle = this._boxBorderColor
        ctx.lineWidth = this._boxBorderWidth / (this.scaleX || 1)
        ctx.strokeRect(-this.width / 2 - pad, -this.height / 2 - pad, w, h)
        ctx.restore()
      }
    }

    this._applyZoom()

    this._resizeHandler = () => this._applyZoom()
    window.addEventListener('resize', this._resizeHandler)

    setTimeout(() => this._applyZoom(), 100)
    setTimeout(() => this._applyZoom(), 300)
    setTimeout(() => this._applyZoom(), 600)

    if (preloaded) {
      const img = preloaded
      this.bgImage = img
      const scale = Math.max(this.CANVAS_SIZE / img.width, this.CANVAS_SIZE / img.height)
      img.set({
        scaleX: scale,
        scaleY: scale,
        originX: 'center',
        originY: 'center',
        left: this.CANVAS_SIZE / 2,
        top: this.CANVAS_SIZE / 2
      })
      this._startCompose()
      this._reportImageLoaded()
    } else {
      this._startEditNoImage()
    }
    return true
  }

  exportCanvasState() {
    if (!this.fabricCanvas) return ''
    return JSON.stringify(this.fabricCanvas.toJSON([
      'name', 'selectable', 'evented', 'editable',
      '_boxBorderWidth', '_boxBorderColor', '_boxBorderPadding'
    ]))
  }

  async initEditorFromState(stateJson) {
    const initToken = this._newInitToken()
    this._clearFabricFailure()
    if (!window.fabric && !(await this._waitForFabric(initToken))) return
    this.destroyCanvas()

    const canvasEl = this.canvasTarget
    this.fabricCanvas = new fabric.Canvas(canvasEl, {
      width: this.CANVAS_SIZE,
      height: this.CANVAS_SIZE,
      backgroundColor: '#1a1a1a',
      preserveObjectStacking: true,
      selection: true
    })

    this.fabricCanvas.on('after:render', () => {
      this._drawClipBorder()
    })

    this._setupCenterGuides()

    // Patch Textbox render for box border (matches other init methods)
    const originalRender = fabric.Textbox.prototype._render
    fabric.Textbox.prototype._render = function(ctx) {
      originalRender.call(this, ctx)
      if (this._boxBorderWidth && this._boxBorderWidth > 0 && this._boxBorderColor) {
        ctx.save()
        const pad = this._boxBorderPadding || 10
        const w = this.width + pad * 2
        const h = this.height + pad * 2
        ctx.strokeStyle = this._boxBorderColor
        ctx.lineWidth = this._boxBorderWidth
        ctx.strokeRect(-this.width / 2 - pad, -this.height / 2 - pad, w, h)
        ctx.restore()
      }
    }

    const originalImageRender = fabric.Image.prototype._render
    fabric.Image.prototype._render = function(ctx) {
      originalImageRender.call(this, ctx)
      if (this._boxBorderWidth && this._boxBorderWidth > 0 && this._boxBorderColor) {
        ctx.save()
        const pad = this._boxBorderPadding || 10
        const w = this.width + pad * 2
        const h = this.height + pad * 2
        ctx.strokeStyle = this._boxBorderColor
        ctx.lineWidth = this._boxBorderWidth / (this.scaleX || 1)
        ctx.strokeRect(-this.width / 2 - pad, -this.height / 2 - pad, w, h)
        ctx.restore()
      }
    }

    this._applyZoom()
    this._resizeHandler = () => this._applyZoom()
    window.addEventListener('resize', this._resizeHandler)

    setTimeout(() => this._applyZoom(), 100)
    setTimeout(() => this._applyZoom(), 300)
    setTimeout(() => this._applyZoom(), 600)

    try {
      const state = typeof stateJson === 'string' ? JSON.parse(stateJson) : stateJson
      this.fabricCanvas.loadFromJSON(state, () => {
        if (this._initStale(initToken)) return  // DEF-6
        this.fabricCanvas.renderAll()
        this._enterEdit()
        this._setPhaseLabel('Edit Existing Clipping')
        this._setStatus('Click any element to edit. Drag to move. Double-click text to change it.')
        // Re-apply zoom after load
        setTimeout(() => {
          this._applyZoom()
          this.fabricCanvas.renderAll()
        }, 100)
      })
    } catch (e) {
      console.error("Failed to load canvas state:", e)
      this._startEditNoImage()
      this._setStatus('Failed to restore saved state. Starting blank edit.')
    }
  }

  // No headline is passed in: the only title an existing clip has is the
  // form's, which is the OUTLET name, and it must never be drawn on the image
  // as a headline (senior committee 2026-09-23).
  async initEditorFromExisting(imageUrl) {
    const initToken = this._newInitToken()
    this._clearFabricFailure()
    if (!window.fabric && !(await this._waitForFabric(initToken))) return
    this.destroyCanvas()
    this.currentTitle = 'Article Title'
    this.currentDescription = ''
    this.currentLogoUrl = null

    const canvasEl = this.canvasTarget
    this.fabricCanvas = new fabric.Canvas(canvasEl, {
      width: this.CANVAS_SIZE,
      height: this.CANVAS_SIZE,
      backgroundColor: '#1a1a1a',
      preserveObjectStacking: true,
      selection: true
    })

    this.fabricCanvas.on('after:render', () => {
      this._drawClipBorder()
    })

    this._setupCenterGuides()

    // Extend Textbox to support box border rendering
    const originalRender = fabric.Textbox.prototype._render
    fabric.Textbox.prototype._render = function(ctx) {
      originalRender.call(this, ctx)
      if (this._boxBorderWidth && this._boxBorderWidth > 0 && this._boxBorderColor) {
        ctx.save()
        const pad = this._boxBorderPadding || 10
        const w = this.width + pad * 2
        const h = this.height + pad * 2
        ctx.strokeStyle = this._boxBorderColor
        ctx.lineWidth = this._boxBorderWidth
        ctx.strokeRect(-this.width / 2 - pad, -this.height / 2 - pad, w, h)
        ctx.restore()
      }
    }

    // Extend Image to support box border rendering
    const originalImageRender = fabric.Image.prototype._render
    fabric.Image.prototype._render = function(ctx) {
      originalImageRender.call(this, ctx)
      if (this._boxBorderWidth && this._boxBorderWidth > 0 && this._boxBorderColor) {
        ctx.save()
        const pad = this._boxBorderPadding || 10
        const w = this.width + pad * 2
        const h = this.height + pad * 2
        ctx.strokeStyle = this._boxBorderColor
        ctx.lineWidth = this._boxBorderWidth / (this.scaleX || 1)
        ctx.strokeRect(-this.width / 2 - pad, -this.height / 2 - pad, w, h)
        ctx.restore()
      }
    }

    this._applyZoom()

    this._resizeHandler = () => this._applyZoom()
    window.addEventListener('resize', this._resizeHandler)

    setTimeout(() => this._applyZoom(), 100)
    setTimeout(() => this._applyZoom(), 300)
    setTimeout(() => this._applyZoom(), 600)

    try {
      const img = await this._loadImage(imageUrl)
      if (this._initStale(initToken)) return  // DEF-6: a newer init owns the canvas now

      // Scale to fill the canvas
      const scale = Math.max(this.CANVAS_SIZE / img.width, this.CANVAS_SIZE / img.height)
      img.set({
        scaleX: scale,
        scaleY: scale,
        left: this.CANVAS_SIZE / 2,
        top: this.CANVAS_SIZE / 2,
        originX: 'center',
        originY: 'center',
        selectable: true,
        evented: true,
        hasControls: true,
        hasBorders: true,
        lockRotation: true,
        lockSkewingX: true,
        lockSkewingY: true,
        name: 'existing-clipping',
        cornerColor: 'rgba(193, 122, 90, 0.9)',
        cornerStyle: 'circle',
        cornerSize: 12,
        transparentCorners: false,
        borderColor: 'rgba(193, 122, 90, 0.7)'
      })
      this.bgImage = img
      this.fabricCanvas.add(img)

      this._enterEdit()
      this._setPhaseLabel('Edit Existing Clipping')
      this._setStatus('Click the image to move or resize it. Use + Text or + Image to add new elements on top.')
    } catch (e) {
      if (this._initStale(initToken)) return  // closed, or a newer init owns the editor
      console.error("Failed to load existing clipping:", e)
      this._reportImageFailure("This clip's saved image could not be loaded. Nothing has been changed; close and try again, or re-generate the clip.")
      this._startEditNoImage()
      this._setStatus('Failed to load existing clipping image.')
    }
  }

  // ════════════════════════════════════════════
  // PHASE 1: COMPOSE (CROP / FRAME)
  // ════════════════════════════════════════════

  _startCompose() {
    this.phase = 'compose'
    this.fabricCanvas.clear()
    this.fabricCanvas.backgroundColor = '#1a1a1a'
    this._clipBorderEnabled = false
    if (this.hasClipBorderToggleTarget) this.clipBorderToggleTarget.classList.remove('active')

    this.bgImage.set({
      selectable: true,
      evented: true,
      hasControls: true,
      hasBorders: true,
      lockRotation: true,
      lockSkewingX: true,
      lockSkewingY: true,
      lockScalingFlip: true,
      cornerColor: 'rgba(193, 122, 90, 0.9)',
      cornerStyle: 'circle',
      cornerSize: 14,
      transparentCorners: false,
      borderColor: 'rgba(193, 122, 90, 0.7)',
      borderScaleFactor: 2
    })
    this.bgImage.setControlsVisibility({
      mt: false, mb: false, ml: false, mr: false,
      tl: true, tr: true, bl: true, br: true,
      mtr: false
    })

    this.fabricCanvas.add(this.bgImage)
    this.fabricCanvas.setActiveObject(this.bgImage)
    this.fabricCanvas.requestRenderAll()

    setTimeout(() => {
      this._applyZoom()
      this.fabricCanvas.requestRenderAll()
    }, 100)

    this._showComposeBar()
    this._setPhaseLabel('Step 1: Frame Your Image')
    this._setStatus('Drag to reposition. Corner handles to scale. Then click "Lock Background & Add Text".')
  }

  // ════════════════════════════════════════════
  // TRANSITION: COMPOSE → EDIT
  // ════════════════════════════════════════════

  lockAndEdit() {
    if (this.phase !== 'compose' || !this.bgImage) return

    this.fabricCanvas.discardActiveObject()
    this.fabricCanvas.renderAll()

    const currentZoom = this.fabricCanvas.getZoom()
    this.fabricCanvas.setZoom(1)
    this.fabricCanvas.setDimensions({
      width: this.CANVAS_SIZE,
      height: this.CANVAS_SIZE
    })
    this.fabricCanvas.renderAll()

    const croppedDataUrl = this.fabricCanvas.toDataURL({
      format: 'png',
      quality: 1
    })

    this.fabricCanvas.clear()
    this.fabricCanvas.backgroundColor = '#1a1a1a'

    this.fabricCanvas.setZoom(currentZoom)
    this.fabricCanvas.setDimensions({
      width: this.CANVAS_SIZE * currentZoom,
      height: this.CANVAS_SIZE * currentZoom
    })

    fabric.Image.fromURL(croppedDataUrl, (croppedImg) => {
      if (!croppedImg || !croppedImg.width) {
        console.error("Failed to load cropped image")
        this._addTextObjects()
        this._enterEdit()
        return
      }

      croppedImg.set({
        left: 0,
        top: 0,
        originX: 'left',
        originY: 'top',
        selectable: false,
        evented: false
      })
      this.fabricCanvas.add(croppedImg)

      this._addTextObjects()
      if (this.currentLogoUrl) this._addLogo(this.currentLogoUrl)
      this._enterEdit()
    }, { crossOrigin: 'anonymous' })
  }

  _addTextObjects() {
    const defaults = {
      editable: true,
      selectable: true,
      evented: true,
      cornerColor: 'rgba(193, 122, 90, 0.9)',
      cornerStyle: 'circle',
      cornerSize: 12,
      transparentCorners: false,
      borderColor: 'rgba(193, 122, 90, 0.7)'
    }

    const titleText = new fabric.Textbox(this.currentTitle, {
      ...defaults,
      left: 60,
      top: this.CANVAS_SIZE - 300,
      width: this.CANVAS_SIZE - 120,
      fontFamily: 'Playfair Display',
      fontSize: 48,
      fontWeight: '700',
      fill: '#ffffff',
      lineHeight: 1.2,
      name: 'title'
    })
    this.fabricCanvas.add(titleText)

    const descText = new fabric.Textbox(this.currentDescription, {
      ...defaults,
      left: 60,
      top: this.CANVAS_SIZE - 160,
      width: this.CANVAS_SIZE - 120,
      fontFamily: 'Inter',
      fontSize: 24,
      fontWeight: '400',
      fill: 'rgba(255, 255, 255, 0.85)',
      lineHeight: 1.4,
      name: 'description'
    })
    this.fabricCanvas.add(descText)
  }

  async _addLogo(logoUrl) {
    try {
      // Normalization lives in _loadImage's choke point (DEF-3).
      const logo = await this._loadImage(logoUrl)
      const maxH = 80
      const s = Math.min(maxH / logo.height, 200 / logo.width)
      logo.set({
        scaleX: s,
        scaleY: s,
        left: this.CANVAS_SIZE - 60,
        top: 40,
        originX: 'right',
        selectable: true,
        evented: true,
        name: 'logo',
        cornerColor: 'rgba(193, 122, 90, 0.9)',
        cornerStyle: 'circle',
        cornerSize: 10,
        transparentCorners: false,
        borderColor: 'rgba(193, 122, 90, 0.7)',
        strokeUniform: true,
        paintFirst: 'stroke'
      })
      this.fabricCanvas.add(logo)
      this.fabricCanvas.renderAll()
    } catch (e) {
      console.error("Logo load failed:", e)
    }
  }

  // A logo chosen after the editor opened (Upload Logo). While framing,
  // remembering it is enough: Lock places currentLogoUrl. Once editing, the
  // logo on the canvas is swapped where she can see it (committee
  // 2026-09-24: it used to reach the clip only after Reset).
  async setLogo(url) {
    this.currentLogoUrl = url
    if (!this.fabricCanvas || this.phase !== 'edit') return
    this.fabricCanvas.getObjects().filter(o => o.name === 'logo').forEach(o => this.fabricCanvas.remove(o))
    if (url) await this._addLogo(url)
    this._saveHistory()
  }

  _enterEdit() {
    this.phase = 'edit'

    this.fabricCanvas.on('selection:created', (e) => this._syncToolbar(e.selected?.[0]))
    this.fabricCanvas.on('selection:updated', (e) => this._syncToolbar(e.selected?.[0]))
    this.fabricCanvas.on('selection:cleared', () => this._clearToolbar())
    // Support #925: while typing inside a box, the toolbar follows the
    // highlighted range (per-character styles), not the whole box.
    this.fabricCanvas.on('text:selection:changed', (e) => this._syncToolbar(e.target))
    this.fabricCanvas.on('text:editing:exited', (e) => this._syncToolbar(e.target))

    this._setupKeyboard()
    this._showEditBar()
    this._setPhaseLabel('Step 2: Edit Your Clip')
    this._setStatus('Click text to select. Drag to move. Double-click to edit. Use toolbar below.')

    // Reset canvas-level controls
    if (this.hasOverlaySelectTarget) this.overlaySelectTarget.value = 'none'
    this._clipBorderEnabled = false
    if (this.hasClipBorderToggleTarget) this.clipBorderToggleTarget.classList.remove('active')

    this.fabricCanvas.requestRenderAll()
    setTimeout(() => {
      this._applyZoom()
      this.fabricCanvas.requestRenderAll()
    }, 100)
    setTimeout(() => this._applyZoom(), 300)

    this._saveHistory()
  }

  // No background. Usually because the outlet refused us (register
  // 2026-09-22), so this is the state a staffer finishes a blocked clip
  // in, and it has to say so: "Step 2" claimed a step 1 that never
  // happened, and the edit-mode advice assumed a photo.
  _startEditNoImage() {
    this.fabricCanvas.clear()
    this.fabricCanvas.backgroundColor = '#2a2a2a'
    this._addTextObjects()
    if (this.currentLogoUrl) this._addLogo(this.currentLogoUrl)
    this._enterEdit()
    this._textOnly = true
    this._setPhaseLabel('Text only until you add a background')
    this._setStatus('No background yet. Type the title and description in the fields above, then pick a DPK photo, upload an image, or copy one from the article and press Cmd+V.')
  }

  // ONE field changed, so only its text moves: into the text Lock will
  // render and into its box on the canvas, whenever that box exists. The
  // field and the image headline are one thing with two places to type it,
  // and the LAST edit wins in both (senior committee 2026-09-23). Keeping
  // them apart meant Reset rebuilt from one and Re-frame from the other,
  // and each could lose a correction she had just made.
  setText(name, text) {
    const fallback = name === 'title' ? 'Article Title' : 'Article description'
    if (name === 'title') this.currentTitle = text || fallback
    else this.currentDescription = text || fallback
    if (!this.fabricCanvas) return

    const box = this.fabricCanvas.getObjects().find(o => o.name === name)
    if (!box) return
    box.set('text', text || fallback)
    this.fabricCanvas.requestRenderAll()
  }

  // Send to TEAM fills a box only while it still shows its placeholder:
  // words she typed on the image are hers (committee seat A, 2026-10-01).
  fillText(name, text) {
    if (this._typedText()[name]) return
    this.setText(name, text)
  }

  // A picture is on the canvas (a lookup's, hers, or a DPK photo), as
  // opposed to the text-only state a refused outlet leaves.
  get hasBackground() {
    return !!this.fabricCanvas && !this._textOnly
  }

  // What is written in the title and description boxes, unless it is
  // still the placeholder. Empty strings when there is no canvas.
  _typedText() {
    const out = { title: '', description: '' }
    if (!this.fabricCanvas) return out
    for (const obj of this.fabricCanvas.getObjects()) {
      const text = (obj.text || '').trim()
      if (obj.name === 'title' && text !== 'Article Title') out.title = text
      if (obj.name === 'description' && text !== 'Article description') out.description = text
    }
    return out
  }

  backToCompose() {
    if (!this.bgImage) return
    // Re-framing clears the text boxes and Lock rebuilds them from these,
    // so carry across what she typed on the image first.
    const typed = this._typedText()
    if (typed.title) this.currentTitle = typed.title
    if (typed.description) this.currentDescription = typed.description
    this.fabricCanvas.off()
    this._startCompose()
  }

  // ════════════════════════════════════════════
  // TOOLBAR SYNC
  // ════════════════════════════════════════════

  _syncToolbar(obj) {
    if (!obj || !obj.selectable) {
      this._clearToolbar()
      return
    }

    const isText = obj.type === 'textbox' || obj.type === 'i-text'
    const isImage = obj.type === 'image'

    if (!isText && !isImage) {
      this._clearToolbar()
      return
    }

    // Text-only controls
    if (isText) {
      if (this.hasFontSelectTarget) this.fontSelectTarget.value = this._textStyle(obj, 'fontFamily') || 'Inter'
      if (this.hasFontSizeSelectTarget) this.fontSizeSelectTarget.value = String(Math.round(this._textStyle(obj, 'fontSize') || 24))
      if (this.hasColorPickerTarget) this.colorPickerTarget.value = this._toHex(this._textStyle(obj, 'fill')) || '#ffffff'
      if (this.hasBoldBtnTarget) this.boldBtnTarget.classList.toggle('active', this._isBold(this._textStyle(obj, 'fontWeight')))
      if (this.hasItalicBtnTarget) this.italicBtnTarget.classList.toggle('active', this._textStyle(obj, 'fontStyle') === 'italic')
      if (this.hasUnderlineBtnTarget) this.underlineBtnTarget.classList.toggle('active', !!this._textStyle(obj, 'underline'))
      if (this.hasStrikeBtnTarget) this.strikeBtnTarget.classList.toggle('active', !!this._textStyle(obj, 'linethrough'))

      const align = obj.textAlign || 'left'
      if (this.hasAlignLeftBtnTarget) this.alignLeftBtnTarget.classList.toggle('active', align === 'left')
      if (this.hasAlignCenterBtnTarget) this.alignCenterBtnTarget.classList.toggle('active', align === 'center')
      if (this.hasAlignRightBtnTarget) this.alignRightBtnTarget.classList.toggle('active', align === 'right')

      if (this.hasHighlightPickerTarget) this.highlightPickerTarget.value = this._toHex(obj.backgroundColor) || '#000000'
    }

    // Shared controls: box border, outline, shadow, opacity (text + image)
    const hasBox = obj._boxBorderWidth && obj._boxBorderWidth > 0
    if (this.hasBoxBorderToggleTarget) this.boxBorderToggleTarget.classList.toggle('active', hasBox)
    if (this.hasBoxBorderWidthTarget) this.boxBorderWidthTarget.value = obj._boxBorderWidth || 3
    if (this.hasBoxBorderColorTarget) this.boxBorderColorTarget.value = obj._boxBorderColor || '#ffffff'
    if (this.hasBoxBorderPaddingTarget) this.boxBorderPaddingTarget.value = obj._boxBorderPadding || 10

    if (this.hasOutlineToggleTarget) this.outlineToggleTarget.classList.toggle('active', !!obj.stroke && obj.strokeWidth > 0)
    if (this.hasOutlineWidthTarget) this.outlineWidthTarget.value = obj.strokeWidth || 2
    if (this.hasOutlineColorTarget) this.outlineColorTarget.value = this._toHex(obj.stroke) || '#000000'

    const sh = obj.shadow
    if (this.hasShadowToggleTarget) this.shadowToggleTarget.classList.toggle('active', !!sh)
    if (sh) {
      if (this.hasShadowBlurTarget) this.shadowBlurTarget.value = sh.blur || 15
      if (this.hasShadowOffsetXTarget) this.shadowOffsetXTarget.value = sh.offsetX || 6
      if (this.hasShadowOffsetYTarget) this.shadowOffsetYTarget.value = sh.offsetY || 6
      if (this.hasShadowColorTarget) this.shadowColorTarget.value = this._toHex(sh.color) || '#000000'
    }

    if (this.hasOpacitySliderTarget) this.opacitySliderTarget.value = Math.round((obj.opacity || 1) * 100)
    if (this.hasOpacityValueTarget) this.opacityValueTarget.textContent = Math.round((obj.opacity || 1) * 100) + '%'
  }

  _clearToolbar() {
    ;['boldBtn', 'italicBtn', 'underlineBtn', 'strikeBtn',
      'alignLeftBtn', 'alignCenterBtn', 'alignRightBtn', 'outlineToggle', 'shadowToggle', 'boxBorderToggle'
    ].forEach(name => {
      const hasTarget = `has${name.charAt(0).toUpperCase() + name.slice(1)}Target`
      if (this[hasTarget]) this[`${name}Target`].classList.remove('active')
    })
  }

  // ════════════════════════════════════════════
  // TOOLBAR ACTIONS
  // ════════════════════════════════════════════

  _getTextObj() {
    const obj = this.fabricCanvas?.getActiveObject()
    return (obj && (obj.type === 'textbox' || obj.type === 'i-text')) ? obj : null
  }

  _getAnyObj() {
    return this.fabricCanvas?.getActiveObject()
  }

  _getStylableObj() {
    const obj = this.fabricCanvas?.getActiveObject()
    if (!obj || !obj.selectable) return null
    if (obj.type === 'textbox' || obj.type === 'i-text' || obj.type === 'image') return obj
    return null
  }

  // Support #925 (Taylor, 2026-08-17): italicizing an album name restyled
  // the whole clipping, because every setter wrote the OBJECT's property.
  // While a box is being edited with a highlighted range, styling now
  // applies to that range only (Fabric per-character styles); with the
  // whole box selected it applies to the box AND clears that property's
  // per-character overrides, so a whole-box toggle always wins visibly.
  _hasTextSelection(o) {
    return !!(o && o.isEditing && o.selectionStart !== o.selectionEnd)
  }

  _textStyle(o, prop) {
    if (this._hasTextSelection(o)) {
      const styles = o.getSelectionStyles(o.selectionStart, o.selectionEnd, true)
      if (styles.length && styles[0][prop] !== undefined) return styles[0][prop]
    }
    return o[prop]
  }

  _setTextStyle(o, prop, value) {
    if (this._hasTextSelection(o)) {
      o.setSelectionStyles({ [prop]: value }, o.selectionStart, o.selectionEnd)
    } else {
      o.removeStyle(prop)
      o.set(prop, value)
    }
    this.fabricCanvas.renderAll(); this._saveHistory()
  }

  _isBold(weight) { return weight === '700' || weight === 700 || weight === 'bold' }

  changeFont(e) { const o = this._getTextObj(); if (o) this._setTextStyle(o, 'fontFamily', e.target.value) }
  changeFontSize(e) { const o = this._getTextObj(); if (o) this._setTextStyle(o, 'fontSize', parseInt(e.target.value) || 24) }
  changeColor(e) { const o = this._getTextObj(); if (o) this._setTextStyle(o, 'fill', e.target.value) }
  changeHighlight(e) { const o = this._getTextObj(); if (o) { o.set('backgroundColor', e.target.value); this.fabricCanvas.renderAll(); this._saveHistory() } }
  clearHighlight() { const o = this._getTextObj(); if (o) { o.set('backgroundColor', ''); this.fabricCanvas.renderAll(); this._saveHistory() } }

  toggleBold() {
    const o = this._getTextObj(); if (!o) return
    const now = this._isBold(this._textStyle(o, 'fontWeight'))
    this._setTextStyle(o, 'fontWeight', now ? '400' : '700')
    if (this.hasBoldBtnTarget) this.boldBtnTarget.classList.toggle('active', !now)
  }

  toggleItalic() {
    const o = this._getTextObj(); if (!o) return
    const now = this._textStyle(o, 'fontStyle') === 'italic'
    this._setTextStyle(o, 'fontStyle', now ? 'normal' : 'italic')
    if (this.hasItalicBtnTarget) this.italicBtnTarget.classList.toggle('active', !now)
  }

  toggleUnderline() {
    const o = this._getTextObj(); if (!o) return
    const now = !!this._textStyle(o, 'underline')
    this._setTextStyle(o, 'underline', !now)
    if (this.hasUnderlineBtnTarget) this.underlineBtnTarget.classList.toggle('active', !now)
  }

  toggleStrike() {
    const o = this._getTextObj(); if (!o) return
    const now = !!this._textStyle(o, 'linethrough')
    this._setTextStyle(o, 'linethrough', !now)
    if (this.hasStrikeBtnTarget) this.strikeBtnTarget.classList.toggle('active', !now)
  }

  setAlign(e) {
    const o = this._getTextObj(); if (!o) return
    const align = e.currentTarget.dataset.align || 'left'
    o.set('textAlign', align)
    if (this.hasAlignLeftBtnTarget) this.alignLeftBtnTarget.classList.toggle('active', align === 'left')
    if (this.hasAlignCenterBtnTarget) this.alignCenterBtnTarget.classList.toggle('active', align === 'center')
    if (this.hasAlignRightBtnTarget) this.alignRightBtnTarget.classList.toggle('active', align === 'right')
    this.fabricCanvas.renderAll(); this._saveHistory()
  }

  toggleOutline() {
    const o = this._getStylableObj(); if (!o) return
    const hasOutline = !!o.stroke && o.strokeWidth > 0
    if (hasOutline) {
      o.set({ stroke: null, strokeWidth: 0 })
    } else {
      o.set({
        stroke: this.hasOutlineColorTarget ? this.outlineColorTarget.value : '#000000',
        strokeWidth: this.hasOutlineWidthTarget ? parseInt(this.outlineWidthTarget.value) || 2 : 2,
        strokeUniform: true,
        paintFirst: 'stroke'
      })
    }
    if (this.hasOutlineToggleTarget) this.outlineToggleTarget.classList.toggle('active', !hasOutline)
    o.dirty = true
    this.fabricCanvas.renderAll(); this._saveHistory()
  }

  changeOutlineWidth(e) {
    const o = this._getStylableObj()
    if (o && o.stroke) {
      o.set('strokeWidth', parseInt(e.target.value) || 1)
      o.dirty = true
      this.fabricCanvas.renderAll(); this._saveHistory()
    }
  }

  changeOutlineColor(e) {
    const o = this._getStylableObj()
    if (o && o.stroke) {
      o.set('stroke', e.target.value)
      o.dirty = true
      this.fabricCanvas.renderAll(); this._saveHistory()
    }
  }

  // ── Box Border ──

  toggleBoxBorder() {
    const o = this._getStylableObj(); if (!o) return
    const has = o._boxBorderWidth && o._boxBorderWidth > 0
    if (has) {
      o._boxBorderWidth = 0
    } else {
      o._boxBorderWidth = this.hasBoxBorderWidthTarget ? parseInt(this.boxBorderWidthTarget.value) || 3 : 3
      o._boxBorderColor = this.hasBoxBorderColorTarget ? this.boxBorderColorTarget.value : '#ffffff'
      o._boxBorderPadding = this.hasBoxBorderPaddingTarget ? parseInt(this.boxBorderPaddingTarget.value) || 10 : 10
    }
    if (this.hasBoxBorderToggleTarget) this.boxBorderToggleTarget.classList.toggle('active', !has)
    o.dirty = true
    this.fabricCanvas.renderAll(); this._saveHistory()
  }

  changeBoxBorderWidth(e) {
    const o = this._getStylableObj()
    if (o && o._boxBorderWidth > 0) {
      o._boxBorderWidth = parseInt(e.target.value) || 1
      o.dirty = true; this.fabricCanvas.renderAll(); this._saveHistory()
    }
  }

  changeBoxBorderColor(e) {
    const o = this._getStylableObj()
    if (o && o._boxBorderWidth > 0) {
      o._boxBorderColor = e.target.value
      o.dirty = true; this.fabricCanvas.renderAll(); this._saveHistory()
    }
  }

  changeBoxBorderPadding(e) {
    const o = this._getStylableObj()
    if (o && o._boxBorderWidth > 0) {
      o._boxBorderPadding = parseInt(e.target.value) || 0
      o.dirty = true; this.fabricCanvas.renderAll(); this._saveHistory()
    }
  }

  // ── Shadow ──

  toggleShadow() {
    const o = this._getStylableObj(); if (!o) return
    if (o.shadow) {
      o.set('shadow', null)
    } else {
      o.set('shadow', new fabric.Shadow({
        color: this.hasShadowColorTarget ? this.shadowColorTarget.value : 'rgba(0,0,0,0.8)',
        blur: this.hasShadowBlurTarget ? parseInt(this.shadowBlurTarget.value) || 15 : 15,
        offsetX: this.hasShadowOffsetXTarget ? parseInt(this.shadowOffsetXTarget.value) || 6 : 6,
        offsetY: this.hasShadowOffsetYTarget ? parseInt(this.shadowOffsetYTarget.value) || 6 : 6
      }))
    }
    if (this.hasShadowToggleTarget) this.shadowToggleTarget.classList.toggle('active', !!o.shadow)
    o.dirty = true
    this.fabricCanvas.renderAll(); this._saveHistory()
  }

  changeShadowBlur(e) { this._updShadow('blur', parseInt(e.target.value)) }
  changeShadowOffsetX(e) { this._updShadow('offsetX', parseInt(e.target.value)) }
  changeShadowOffsetY(e) { this._updShadow('offsetY', parseInt(e.target.value)) }
  changeShadowColor(e) { this._updShadow('color', e.target.value) }

  _updShadow(prop, val) {
    const o = this._getStylableObj()
    if (!o) return
    // Auto-create shadow if it doesn't exist yet
    if (!o.shadow) {
      o.set('shadow', new fabric.Shadow({
        color: this.hasShadowColorTarget ? this.shadowColorTarget.value : 'rgba(0,0,0,0.8)',
        blur: 15, offsetX: 6, offsetY: 6
      }))
      if (this.hasShadowToggleTarget) this.shadowToggleTarget.classList.add('active')
    }
    o.shadow[prop] = val
    o.dirty = true
    this.fabricCanvas.renderAll()
    this._saveHistory()
  }

  changeOpacity(e) {
    const o = this._getAnyObj(); if (!o || !o.selectable) return
    const val = parseInt(e.target.value) / 100
    o.set('opacity', val)
    if (this.hasOpacityValueTarget) this.opacityValueTarget.textContent = e.target.value + '%'
    this.fabricCanvas.renderAll(); this._saveHistory()
  }

  // ── Overlay ──

  changeOverlay(e) {
    const val = e.target.value
    const fills = {
      none: null,
      dark: 'rgba(0, 0, 0, 0.35)',
      light: 'rgba(255, 255, 255, 0.35)',
      brand: 'rgba(42, 42, 46, 0.6)'
    }
    const fill = fills[val] || null

    // Remove existing overlay
    if (this._overlayRect) {
      this.fabricCanvas.remove(this._overlayRect)
      this._overlayRect = null
    }

    if (fill) {
      this._overlayRect = new fabric.Rect({
        width: this.CANVAS_SIZE,
        height: this.CANVAS_SIZE,
        fill: fill,
        selectable: false,
        evented: false,
        originX: 'left',
        originY: 'top',
        name: '_overlay'
      })
      // Insert overlay just above the background image (index 1)
      const objects = this.fabricCanvas.getObjects()
      const insertIndex = objects.length > 0 ? 1 : 0
      this.fabricCanvas.insertAt(this._overlayRect, insertIndex)
    }

    this.fabricCanvas.renderAll()
    this._saveHistory()
  }

  // ── Clip Border ──

  toggleClipBorder() {
    this._clipBorderEnabled = !this._clipBorderEnabled
    if (this.hasClipBorderToggleTarget) {
      this.clipBorderToggleTarget.classList.toggle('active', this._clipBorderEnabled)
    }
    this.fabricCanvas.renderAll()
    this._saveHistory()
  }

  changeClipBorderWidth() {
    if (!this._clipBorderEnabled) return
    this.fabricCanvas.renderAll()
  }

  changeClipBorderColor() {
    if (!this._clipBorderEnabled) return
    this.fabricCanvas.renderAll()
  }

  _drawClipBorder() {
    if (!this._clipBorderEnabled) return
    if (!this.fabricCanvas) return

    const ctx = this.fabricCanvas.getContext('2d')
    const zoom = this.fabricCanvas.getZoom()
    const width = this.hasClipBorderWidthTarget ? parseInt(this.clipBorderWidthTarget.value) || 20 : 20
    const color = this.hasClipBorderColorTarget ? this.clipBorderColorTarget.value : '#1a1a1a'

    // Draw border at canvas resolution, scaled by zoom
    const scaledWidth = width * zoom
    const canvasW = this.CANVAS_SIZE * zoom
    const canvasH = this.CANVAS_SIZE * zoom

    ctx.save()
    ctx.strokeStyle = color
    ctx.lineWidth = scaledWidth
    ctx.strokeRect(
      scaledWidth / 2,
      scaledWidth / 2,
      canvasW - scaledWidth,
      canvasH - scaledWidth
    )
    ctx.restore()
  }

  _setupCenterGuides() {
    this._guideX = false
    this._guideY = false
    const threshold = 3
    const mid = this.CANVAS_SIZE / 2

    this.fabricCanvas.on('object:moving', (e) => {
      const obj = e.target
      if (!obj) return
      const center = obj.getCenterPoint()
      let sx = false
      let sy = false
      if (Math.abs(center.x - mid) < threshold) {
        obj.set('left', obj.left + (mid - center.x))
        sx = true
      }
      if (Math.abs(center.y - mid) < threshold) {
        obj.set('top', obj.top + (mid - center.y))
        sy = true
      }
      if (sx || sy) obj.setCoords()
      this._guideX = sx
      this._guideY = sy
    })

    this.fabricCanvas.on('mouse:up', () => {
      if (this._guideX || this._guideY) {
        this._guideX = false
        this._guideY = false
        this.fabricCanvas.requestRenderAll()
      }
    })

    this.fabricCanvas.on('after:render', () => this._drawCenterGuides())
  }

  _drawCenterGuides() {
    if (!this._guideX && !this._guideY) return
    if (!this.fabricCanvas) return

    const ctx = this.fabricCanvas.getContext('2d')
    const zoom = this.fabricCanvas.getZoom()
    const mid = (this.CANVAS_SIZE / 2) * zoom
    const canvasW = this.CANVAS_SIZE * zoom
    const canvasH = this.CANVAS_SIZE * zoom

    ctx.save()
    ctx.strokeStyle = '#00d4ff'
    ctx.lineWidth = 1

    if (this._guideX) {
      ctx.beginPath()
      ctx.moveTo(mid, 0)
      ctx.lineTo(mid, canvasH)
      ctx.stroke()
    }
    if (this._guideY) {
      ctx.beginPath()
      ctx.moveTo(0, mid)
      ctx.lineTo(canvasW, mid)
      ctx.stroke()
    }

    ctx.restore()
  }

  bringForward() { const o = this._getAnyObj(); if (o && o.selectable) { this.fabricCanvas.bringForward(o); this._ensureClipBorderOnTop(); this.fabricCanvas.renderAll() } }
  sendBackward() { const o = this._getAnyObj(); if (o && o.selectable) { this.fabricCanvas.sendBackwards(o); this._ensureClipBorderOnTop(); this.fabricCanvas.renderAll() } }

  duplicateSelected() {
    const o = this._getAnyObj()
    if (!o || !o.selectable) return
    o.clone((cloned) => {
      cloned.set({ left: o.left + 30, top: o.top + 30 })
      this.fabricCanvas.add(cloned)
      this.fabricCanvas.setActiveObject(cloned)
      this.fabricCanvas.renderAll()
      this._saveHistory()
    })
  }

  toggleLock() {
    const o = this._getAnyObj(); if (!o) return
    const locked = !o.selectable || o.lockMovementX
    o.set({
      selectable: locked, evented: locked,
      lockMovementX: !locked, lockMovementY: !locked,
      hasControls: locked
    })
    this.fabricCanvas.renderAll()
  }

  addText() {
    if (this.phase !== 'edit') return
    const text = new fabric.Textbox('New Text', {
      left: 100, top: 100, width: 400,
      fontFamily: 'Inter', fontSize: 32, fill: '#ffffff',
      editable: true, selectable: true, evented: true,
      cornerColor: 'rgba(193, 122, 90, 0.9)',
      cornerStyle: 'circle', cornerSize: 12,
      transparentCorners: false,
      borderColor: 'rgba(193, 122, 90, 0.7)'
    })
    this.fabricCanvas.add(text)
    this.fabricCanvas.setActiveObject(text)
    this.fabricCanvas.renderAll()
    this._saveHistory()
  }

  deleteSelected() {
    const o = this._getAnyObj()
    if (o && o.selectable && o.name !== 'existing-clipping' && o !== this.bgImage) {
      this.fabricCanvas.remove(o)
      this.fabricCanvas.discardActiveObject()
      this.fabricCanvas.renderAll()
      this._saveHistory()
      this._setStatus('Object deleted. Press Ctrl+Z to undo.')
      if (this._undoHintTimer) clearTimeout(this._undoHintTimer)
      this._undoHintTimer = setTimeout(() => {
        this._setStatus('Click text to select. Drag to move. Double-click to edit. Use toolbar below.')
      }, 4000)
    }
  }

  toggleMore() {
    if (this.hasAdvancedBarTarget) {
      const isHidden = this.advancedBarTarget.classList.toggle('hidden')
      if (this.hasMoreToggleTarget) {
        this.moreToggleTarget.classList.toggle('active', !isHidden)
      }
      // Also toggle image insert panel with advanced bar
      if (this.hasImageInsertPanelTarget) {
        if (isHidden) {
          this.imageInsertPanelTarget.classList.add('hidden')
          if (this.hasImageMenuBtnTarget) this.imageMenuBtnTarget.classList.remove('active')
        } else {
          this.imageInsertPanelTarget.classList.remove('hidden')
          if (this.hasImageMenuBtnTarget) this.imageMenuBtnTarget.classList.add('active')
        }
      }
      // Update Row 1 border radius based on whether Row 2 is visible
      const row1 = this.advancedBarTarget.previousElementSibling
      if (row1) {
        if (isHidden) {
          row1.classList.remove('rounded-t-xl')
          row1.classList.add('rounded-xl')
        } else {
          row1.classList.remove('rounded-xl')
          row1.classList.add('rounded-t-xl')
        }
      }
    }
  }

  // ════════════════════════════════════════════
  // UNDO / REDO
  // ════════════════════════════════════════════

  _saveHistory() {
    if (this.skipHistory) return
    this._ensureClipBorderOnTop()
    const json = JSON.stringify(this.fabricCanvas.toJSON([
      'name', 'selectable', 'evented', 'editable',
      '_boxBorderWidth', '_boxBorderColor', '_boxBorderPadding'
    ]))
    if (this.historyIndex < this.history.length - 1) {
      this.history = this.history.slice(0, this.historyIndex + 1)
    }
    this.history.push(json)
    if (this.history.length > 30) this.history.shift()
    this.historyIndex = this.history.length - 1
  }

  undo() {
    if (this.historyIndex <= 0) return
    this.historyIndex--
    this._loadHistoryState(this.history[this.historyIndex])
  }

  redo() {
    if (this.historyIndex >= this.history.length - 1) return
    this.historyIndex++
    this._loadHistoryState(this.history[this.historyIndex])
  }

  _loadHistoryState(json) {
    this.skipHistory = true
    this.fabricCanvas.loadFromJSON(json, () => {
      this.fabricCanvas.renderAll()
      this.skipHistory = false
    })
  }

  // ════════════════════════════════════════════
  // EXPORT
  // ════════════════════════════════════════════

  async exportBlob() {
    this._guideX = false
    this._guideY = false
    this.fabricCanvas.discardActiveObject()
    const currentZoom = this.fabricCanvas.getZoom()
    this.fabricCanvas.setZoom(1)
    this.fabricCanvas.setDimensions({ width: this.CANVAS_SIZE, height: this.CANVAS_SIZE })
    this.fabricCanvas.renderAll()

    return new Promise((resolve, reject) => {
      try {
        this.canvasTarget.toBlob((blob) => {
          this.fabricCanvas.setZoom(currentZoom)
          this.fabricCanvas.setDimensions({
            width: this.CANVAS_SIZE * currentZoom,
            height: this.CANVAS_SIZE * currentZoom
          })
          this.fabricCanvas.renderAll()
          if (blob) resolve(blob)
          else reject(new Error("Export returned null"))
        }, 'image/jpeg', 0.91)
      } catch (e) { reject(e) }
    })
  }

  // ════════════════════════════════════════════
  // INSERT IMAGE
  // ════════════════════════════════════════════

  toggleImageMenu() {
    if (!this.hasImageInsertPanelTarget) return
    const isHidden = this.imageInsertPanelTarget.classList.toggle('hidden')
    if (this.hasImageMenuBtnTarget) {
      this.imageMenuBtnTarget.classList.toggle('active', !isHidden)
    }
    // Update advancedBar border radius when panel toggles
    if (this.hasAdvancedBarTarget) {
      if (isHidden) {
        this.advancedBarTarget.classList.add('rounded-b-xl')
      } else {
        this.advancedBarTarget.classList.remove('rounded-b-xl')
      }
    }
  }

  async showInsertPhotoPicker() {
    if (!this.hasInsertPhotoGridTarget || !this.photosUrlValue) return

    this.insertPhotoGridTarget.innerHTML = `<p class="text-xs text-[var(--color-mc-text-secondary)] col-span-5">Loading photos...</p>`
    if (this.hasInsertPhotoPickerTarget) this.insertPhotoPickerTarget.classList.remove('hidden')

    try {
      const response = await fetch(this.photosUrlValue, {
        headers: { "Accept": "application/json" }
      })
      const data = await response.json()

      if (data.photos && data.photos.length > 0) {
        this.insertPhotoGridTarget.innerHTML = data.photos.map(photo =>
          `<button type="button" class="aspect-[4/3] rounded overflow-hidden border border-[var(--color-mc-border)] hover:border-[var(--color-mc-accent-terracotta)] transition-colors cursor-pointer"
                  data-action="click->clipping-editor#insertDpkPhoto"
                  data-photo-url="${photo.full_url}">
            <img src="${photo.thumb_url}" class="w-full h-full object-cover" />
          </button>`
        ).join("")
      } else {
        this.insertPhotoGridTarget.innerHTML = `<p class="text-xs text-[var(--color-mc-text-secondary)] col-span-5">No DPK photos found.</p>`
      }
    } catch (error) {
      console.error("Failed to load photos:", error)
      this.insertPhotoGridTarget.innerHTML = `<p class="text-xs text-[#E57373] col-span-5">Failed to load photos.</p>`
    }
  }

  // DEF-4: the picker closes only when the insert SUCCEEDS, so a failed
  // pick leaves the user exactly where they were, with a visible error.
  async insertDpkPhoto(event) {
    const button = event.currentTarget
    const photoUrl = button.dataset.photoUrl
    if (!photoUrl) return

    const ok = await this._insertImageOnCanvas(photoUrl)
    if (ok && this.hasInsertPhotoPickerTarget) this.insertPhotoPickerTarget.classList.add('hidden')
  }

  async insertImageFromUrl() {
    if (!this.hasInsertImageUrlTarget) return
    const url = this.insertImageUrlTarget.value.trim()
    if (!url) return

    const ok = await this._insertImageOnCanvas(url)
    if (ok) this.insertImageUrlTarget.value = ''
  }

  // Every way of adding a picture arrives here (paste, drop, Upload, a DPK
  // photo, a pasted URL), so this is where the ONE rule lives: with no
  // background, an image she brings IS the background. It used to land as
  // a small object on top of the placeholder text, to be dragged out to the
  // corners by hand, on every outlet that blocks us (register 2026-09-22).
  // With a background already there, it stays an insert.
  async _insertImageOnCanvas(url) {
    if (this._textOnly) return this.initEditor(url, '', '', this.currentLogoUrl)

    if (this.phase !== 'edit' || !this.fabricCanvas) return

    try {
      const img = await this._loadImage(url)
      // Scale to fit within 40% of canvas
      const maxDim = this.CANVAS_SIZE * 0.4
      const scale = Math.min(maxDim / img.width, maxDim / img.height, 1)
      img.set({
        scaleX: scale,
        scaleY: scale,
        left: this.CANVAS_SIZE / 2,
        top: this.CANVAS_SIZE / 2,
        originX: 'center',
        originY: 'center',
        selectable: true,
        evented: true,
        cornerColor: 'rgba(193, 122, 90, 0.9)',
        cornerStyle: 'circle',
        cornerSize: 12,
        transparentCorners: false,
        borderColor: 'rgba(193, 122, 90, 0.7)',
        name: 'inserted-image',
        strokeUniform: true,
        paintFirst: 'stroke'
      })
      this.fabricCanvas.add(img)
      this.fabricCanvas.setActiveObject(img)
      this.fabricCanvas.renderAll()
      this._saveHistory()
      this._reportImageLoaded()
      return true
    } catch (e) {
      console.error("Failed to insert image:", e)
      this._reportImageFailure("That image could not be loaded. If it came from an outlet's site, save it and use Upload instead.")
      return false
    }
  }

  // ════════════════════════════════════════════
  // IMAGE UPLOAD & DRAG-AND-DROP
  // ════════════════════════════════════════════

  triggerImageUpload() {
    if (this.hasInsertImageUploadTarget) this.insertImageUploadTarget.click()
  }

  uploadImageFile(event) {
    const file = event.target.files[0]
    if (!file) return
    this._readFileAndInsert(file)
    event.target.value = ''
  }

  imageDropOver(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasImageInsertPanelTarget) {
      this.imageInsertPanelTarget.classList.add('ring-2', 'ring-[var(--color-mc-accent-terracotta)]', 'bg-[var(--color-mc-accent-terracotta)]/5')
    }
  }

  imageDropLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasImageInsertPanelTarget) {
      this.imageInsertPanelTarget.classList.remove('ring-2', 'ring-[var(--color-mc-accent-terracotta)]', 'bg-[var(--color-mc-accent-terracotta)]/5')
    }
  }

  handleImageDrop(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasImageInsertPanelTarget) {
      this.imageInsertPanelTarget.classList.remove('ring-2', 'ring-[var(--color-mc-accent-terracotta)]', 'bg-[var(--color-mc-accent-terracotta)]/5')
    }

    const files = event.dataTransfer?.files
    if (!files || files.length === 0) return

    const file = files[0]
    if (!file.type.startsWith('image/')) return
    this._readFileAndInsert(file)
  }

  _readFileAndInsert(file) {
    const reader = new FileReader()
    reader.onload = (e) => this._insertImageOnCanvas(e.target.result)
    reader.readAsDataURL(file)
  }

  // ════════════════════════════════════════════
  // HELPERS
  // ════════════════════════════════════════════

  // THE canvas image choke point (incident 2026-08-03, DEF-3). Every
  // canvas-bound load goes through here, so URL normalization lives here
  // and nowhere else: callers pass raw URLs. Routing through the
  // same-origin proxy means canvas loads never depend on S3 CORS and can
  // never taint the canvas (which would break export/save).
  _canvasUrl(url) {
    if (!url) return url
    if (url.startsWith('data:') || url.startsWith('blob:')) return url
    if (this.hasProxyUrlValue && this.proxyUrlValue && url.startsWith(this.proxyUrlValue)) return url
    if (!this.hasProxyUrlValue || !this.proxyUrlValue) return url
    const absolute = new URL(url, window.location.origin).href
    return `${this.proxyUrlValue}?url=${encodeURIComponent(absolute)}`
  }

  _loadImage(url) {
    return new Promise((resolve, reject) => {
      fabric.Image.fromURL(this._canvasUrl(url), (img) => {
        if (img && img.width > 0) resolve(img)
        else reject(new Error("Image load failed"))
      }, { crossOrigin: 'anonymous' })
    })
  }

  // DEF-4: no image failure may be silent. Surfaces through the parent
  // press-clip-editor's banner (which scrolls itself into view).
  // `url` names the image that failed, so the parent can tell "the
  // article's own image is unusable" (clear it from the form) from "a DPK
  // photo failed" (leave the form alone).
  _reportImageFailure(message, url = null) {
    this.dispatch('imageError', { detail: { message, url }, bubbles: true })
  }

  // The other half: an image that DID load, so a stale failure message can
  // be taken down.
  _reportImageLoaded() {
    this.dispatch('imageLoaded', { bubbles: true })
  }

  _toHex(color) {
    if (!color) return null
    if (color.startsWith('#')) return color.substring(0, 7)
    if (color.startsWith('rgb')) {
      const m = color.match(/(\d+)/g)
      if (m && m.length >= 3) return '#' + m.slice(0, 3).map(n => parseInt(n).toString(16).padStart(2, '0')).join('')
    }
    return color
  }

  _ensureClipBorderOnTop() {
    // No longer needed. Border is drawn on raw canvas after every render.
  }

  _showComposeBar() {
    if (this.hasComposeBarTarget) this.composeBarTarget.classList.remove('hidden')
    if (this.hasEditBarTarget) this.editBarTarget.classList.add('hidden')
    if (this.hasAdvancedBarTarget) this.advancedBarTarget.classList.add('hidden')
    if (this.hasImageInsertPanelTarget) this.imageInsertPanelTarget.classList.add('hidden')
  }

  _showEditBar() {
    if (this.hasComposeBarTarget) this.composeBarTarget.classList.add('hidden')
    if (this.hasEditBarTarget) this.editBarTarget.classList.remove('hidden')
    if (this.hasAdvancedBarTarget) this.advancedBarTarget.classList.remove('hidden')
    if (this.hasImageInsertPanelTarget) this.imageInsertPanelTarget.classList.remove('hidden')
  }

  _setupKeyboard() {
    if (this._keyHandler) document.removeEventListener('keydown', this._keyHandler)

    this._keyHandler = (e) => {
      if (!this.fabricCanvas || this.phase !== 'edit') return

      // Don't intercept when typing in a textbox on the canvas
      const active = this.fabricCanvas.getActiveObject()
      if (active && active.isEditing) return

      // Don't intercept when focus is in an input/textarea/select
      const tag = document.activeElement?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return

      // Delete / Backspace: delete selected object (but not the bg image)
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (active && (active.name === 'existing-clipping' || active === this.bgImage)) return
        e.preventDefault()
        this.deleteSelected()
        return
      }

      // Ctrl/Cmd + Z: undo, Ctrl/Cmd + Shift + Z: redo
      const mod = e.metaKey || e.ctrlKey
      if (mod && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        this.undo()
        return
      }
      if (mod && ((e.key === 'z' && e.shiftKey) || e.key === 'y')) {
        e.preventDefault()
        this.redo()
        return
      }
    }

    document.addEventListener('keydown', this._keyHandler)
  }

  _setPhaseLabel(text) { if (this.hasPhaseLabelTarget) this.phaseLabelTarget.textContent = text }
  _setStatus(text) { if (this.hasStatusTarget) this.statusTarget.textContent = text }
}
